<script setup lang="ts">
import ProductPage from '@/components/ProductPage.vue';
import { type Product, getProductById } from '@/lib/shopify/product.ts';
import { useShopifyPreload } from '@/composables/useShopifyPreload';
import { useSeoMeta, useHead } from '@unhead/vue';
import { useData } from 'vike-vue/useData';
import { onMounted, ref } from 'vue';
import { Head } from 'vike-vue/Head';

const CHARGER_ID = 'gid://shopify/Product/8322259812574';

const data = useData<{ product?: Product }>();
const product = ref<Product | null>(data.product ?? null);
const { getPreloadedProductById } = useShopifyPreload();

// fetch fresh product data on mount as ssg may be stale
onMounted(async () => {
  // Try to get preloaded data first
  const preloadedProduct = await getPreloadedProductById(CHARGER_ID);
  if (preloadedProduct) {
    product.value = preloadedProduct;
  } else {
    // Fall back to regular fetch if no preloaded data
    product.value = await getProductById(CHARGER_ID);
  }
});

useSeoMeta({
  title: () => (product.value?.title ? `${product.value.title} | Fast Charge Your Parsec Skateboard` : '600W Fast Charger | Parsec Skateboards'),
  description: 'Charge your Parsec electric skateboard up to 4x faster with our 600W high-power charger. Features intelligent charging protection and premium build quality for maximum safety and reliability.',
  ogTitle: () => (product.value?.title ? `${product.value.title} - Premium Fast Charger` : '600W Fast Charger - Premium Charging Solution'),
  ogDescription: 'Upgrade your charging experience with our 600W fast charger. Intelligent temperature control, overcurrent protection, and premium components ensure safe and efficient charging.',
  ogType: 'website',
  ogImage: () => product.value?.images?.nodes?.[0]?.url,
  twitterCard: 'summary_large_image',
  twitterTitle: () => (product.value?.title ? `${product.value.title} - Premium Fast Charger` : '600W Fast Charger - Premium Charging Solution'),
  twitterDescription: 'Power up your ride faster with our 600W charger. Built with premium components and multiple safety features for reliable, high-speed charging.',
  twitterImage: () => product.value?.images?.nodes?.[0]?.url,
  // Additional product metadata
  keywords: 'electric skateboard charger, fast charger, 600W charger, Parsec accessories, e-skate charging',
  author: 'Parsec Skateboards',
  robots: 'index, follow'
});

// Add JSON-LD structured data
useHead({
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": "600W Fast Charger",
        "description": "Charge your Parsec electric skateboard up to 4x faster with our 600W high-power charger. Features intelligent charging protection and premium build quality for maximum safety and reliability.",
        "brand": {
          "@type": "Brand",
          "name": "Parsec Skateboards"
        },
        "category": "Electric Skateboard Accessories",
        "offers": {
          "@type": "Offer",
          "url": "https://rideparsec.com/products/600w-fast-charger",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        }
      })
    }
  ]
});
</script>

<template>
  <Head>
    <title>{{ product?.title ? `${product.title} | Fast Charge Your Parsec Skateboard` : '600W Fast Charger | Parsec Skateboards' }}</title>
    <meta name="description" content="Charge your Parsec electric skateboard up to 4x faster with our 600W high-power charger. Features intelligent charging protection and premium build quality for maximum safety and reliability." />
    <meta property="og:title" :content="product?.title ? `${product.title} - Premium Fast Charger` : '600W Fast Charger - Premium Charging Solution'" />
    <meta property="og:description" content="Upgrade your charging experience with our 600W fast charger. Intelligent temperature control, overcurrent protection, and premium components ensure safe and efficient charging." />
    <meta property="og:type" content="website" />
    <meta property="og:image" :content="product?.images?.nodes?.[0]?.url" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" :content="product?.title ? `${product.title} - Premium Fast Charger` : '600W Fast Charger - Premium Charging Solution'" />
    <meta name="twitter:description" content="Power up your ride faster with our 600W charger. Built with premium components and multiple safety features for reliable, high-speed charging." />
    <meta name="twitter:image" :content="product?.images?.nodes?.[0]?.url" />
    <!-- Product schema markup -->
    <meta property="product:price:amount" :content="product?.priceRange?.minVariantPrice?.amount" />
    <meta property="product:price:currency" :content="product?.priceRange?.minVariantPrice?.currencyCode" />
    <meta property="product:availability" content="in_stock" />
  </Head>
  <ProductPage v-if="product" :product="product" />
</template>
